/**
 * Invertible arrange / transport patches (Phase 4).
 * Payloads are pattern-sized — never full ClipIR / LabProjectState.
 */
import type { Pattern, PatternCell } from "../../../../src/lib/audio";
import type { PatternLoopRange } from "../lib/patternLoop";
import type { AdamCommand, AdamCommandContext, AdamCommandResult } from "./Command";
import type { RemovedPatternPayload } from "./arrangeBridge";

function requireArrange(context: AdamCommandContext) {
  const arrange = context.arrange;
  if (!arrange) {
    throw new Error("Adam arrange bridge is not registered");
  }
  return arrange;
}

function clonePattern(pattern: Pattern): Pattern {
  return structuredClone(pattern);
}

function cloneCells(cells: PatternCell[]): PatternCell[] {
  return structuredClone(cells);
}

function cloneLoop(range: PatternLoopRange): PatternLoopRange {
  return { start: range.start, length: range.length };
}

export function loopEqual(a: PatternLoopRange, b: PatternLoopRange): boolean {
  return a.start === b.start && a.length === b.length;
}

export class RenamePatternCommand implements AdamCommand {
  readonly id = "renamePattern";
  readonly label = "Rename pattern";

  constructor(
    readonly patternId: string,
    readonly from: string | undefined,
    readonly to: string | undefined,
  ) {}

  execute(context: AdamCommandContext): AdamCommandResult {
    requireArrange(context).renamePattern(this.patternId, this.to);
    return { changed: "arrange" };
  }

  undo(context: AdamCommandContext): AdamCommandResult {
    requireArrange(context).renamePattern(this.patternId, this.from);
    return { changed: "arrange" };
  }

  redo(context: AdamCommandContext): AdamCommandResult {
    return this.execute(context);
  }
}

/** Insert a fully-formed pattern after `afterId` (create / duplicate / paste). */
export class CreatePatternCommand implements AdamCommand {
  readonly id = "createPattern";
  readonly label: string;
  private removed: RemovedPatternPayload | undefined;
  private beforeActive: string | undefined;
  private beforeLoop: PatternLoopRange | undefined;

  constructor(
    readonly pattern: Pattern,
    readonly afterId: string | undefined,
    options?: { label?: string },
  ) {
    this.label = options?.label ?? "Create pattern";
  }

  execute(context: AdamCommandContext): AdamCommandResult {
    const arrange = requireArrange(context);
    if (this.removed) {
      arrange.restorePattern(this.removed);
      this.removed = undefined;
      return { changed: "arrange" };
    }
    this.beforeActive = arrange.getActivePatternId();
    this.beforeLoop = cloneLoop(arrange.getPatternLoop());
    arrange.insertPatternAfter(clonePattern(this.pattern), this.afterId, {
      activePatternId: this.pattern.id,
      snapLoopToNew: true,
    });
    return { changed: "arrange" };
  }

  undo(context: AdamCommandContext): AdamCommandResult {
    const arrange = requireArrange(context);
    this.removed = arrange.removePattern(this.pattern.id) ?? undefined;
    if (!this.removed) {
      throw new Error("Cannot undo create pattern");
    }
    if (this.beforeActive !== undefined && this.beforeLoop) {
      arrange.focusPattern(this.beforeActive, this.beforeLoop);
    }
    return { changed: "arrange" };
  }

  redo(context: AdamCommandContext): AdamCommandResult {
    return this.execute(context);
  }
}

export class DeletePatternCommand implements AdamCommand {
  readonly id = "deletePattern";
  readonly label = "Delete pattern";
  private payload: RemovedPatternPayload | undefined;

  constructor(readonly patternId: string) {}

  execute(context: AdamCommandContext): AdamCommandResult {
    const arrange = requireArrange(context);
    if (!this.payload) {
      this.payload = arrange.removePattern(this.patternId) ?? undefined;
      if (!this.payload) {
        throw new Error("Cannot delete pattern");
      }
    } else {
      const again = arrange.removePattern(this.payload.pattern.id);
      if (!again) {
        throw new Error("Cannot redo delete pattern");
      }
      this.payload = again;
    }
    return { changed: "arrange" };
  }

  undo(context: AdamCommandContext): AdamCommandResult {
    const arrange = requireArrange(context);
    if (!this.payload) {
      throw new Error("DeletePatternCommand has no payload");
    }
    arrange.restorePattern(this.payload);
    return { changed: "arrange" };
  }

  redo(context: AdamCommandContext): AdamCommandResult {
    return this.execute(context);
  }
}

export class ReorderPatternsCommand implements AdamCommand {
  readonly id = "reorderPatterns";
  readonly label = "Reorder patterns";

  constructor(
    readonly fromIndex: number,
    readonly toIndex: number,
  ) {}

  execute(context: AdamCommandContext): AdamCommandResult {
    if (!requireArrange(context).reorderPatterns(this.fromIndex, this.toIndex)) {
      throw new Error("Cannot reorder patterns");
    }
    return { changed: "arrange" };
  }

  undo(context: AdamCommandContext): AdamCommandResult {
    if (!requireArrange(context).reorderPatterns(this.toIndex, this.fromIndex)) {
      throw new Error("Cannot undo reorder patterns");
    }
    return { changed: "arrange" };
  }

  redo(context: AdamCommandContext): AdamCommandResult {
    return this.execute(context);
  }
}

export class PasteTrackCellsCommand implements AdamCommand {
  readonly id = "pasteTrackCells";
  readonly label = "Paste track";

  constructor(
    readonly patternId: string,
    readonly track: number,
    readonly from: PatternCell[],
    readonly to: PatternCell[],
  ) {}

  execute(context: AdamCommandContext): AdamCommandResult {
    requireArrange(context).setTrackCells(this.patternId, this.track, cloneCells(this.to));
    return { changed: "arrange" };
  }

  undo(context: AdamCommandContext): AdamCommandResult {
    requireArrange(context).setTrackCells(this.patternId, this.track, cloneCells(this.from));
    return { changed: "arrange" };
  }

  redo(context: AdamCommandContext): AdamCommandResult {
    return this.execute(context);
  }
}

export class ResizePatternCommand implements AdamCommand {
  readonly id = "resizePattern";
  readonly label = "Pattern length";
  private to: Pattern | undefined;

  constructor(
    readonly patternId: string,
    /** Full pattern snapshot before resize. */
    readonly from: Pattern,
    readonly toSteps: number,
  ) {}

  execute(context: AdamCommandContext): AdamCommandResult {
    const arrange = requireArrange(context);
    if (this.to) {
      arrange.replacePattern(this.patternId, clonePattern(this.to));
    } else {
      arrange.replacePattern(this.patternId, clonePattern(this.from));
      arrange.resizePattern(this.patternId, this.toSteps);
      const next = arrange.getPattern(this.patternId);
      if (!next) {
        throw new Error("Pattern missing after resize");
      }
      this.to = clonePattern(next);
    }
    return { changed: "arrange" };
  }

  undo(context: AdamCommandContext): AdamCommandResult {
    requireArrange(context).replacePattern(this.patternId, clonePattern(this.from));
    return { changed: "arrange" };
  }

  redo(context: AdamCommandContext): AdamCommandResult {
    return this.execute(context);
  }
}

export class SetPatternLoopCommand implements AdamCommand {
  readonly id = "setPatternLoop";
  readonly label = "Loop brace";

  constructor(
    readonly from: PatternLoopRange,
    readonly to: PatternLoopRange,
  ) {}

  execute(context: AdamCommandContext): AdamCommandResult {
    requireArrange(context).setPatternLoop(cloneLoop(this.to));
    return { changed: "patternLoop" };
  }

  undo(context: AdamCommandContext): AdamCommandResult {
    requireArrange(context).setPatternLoop(cloneLoop(this.from));
    return { changed: "patternLoop" };
  }

  redo(context: AdamCommandContext): AdamCommandResult {
    return this.execute(context);
  }

  canMergeWith(command: AdamCommand): boolean {
    return command instanceof SetPatternLoopCommand;
  }

  merge(command: AdamCommand): AdamCommand {
    if (!(command instanceof SetPatternLoopCommand) || !this.canMergeWith(command)) {
      return this;
    }
    return new SetPatternLoopCommand(cloneLoop(this.from), cloneLoop(command.to));
  }
}

export class SetBpmCommand implements AdamCommand {
  readonly id = "setBpm";
  readonly label = "BPM";

  constructor(
    readonly from: number,
    readonly to: number,
  ) {}

  execute(context: AdamCommandContext): AdamCommandResult {
    requireArrange(context).setBpm(this.to);
    return { changed: "transport" };
  }

  undo(context: AdamCommandContext): AdamCommandResult {
    requireArrange(context).setBpm(this.from);
    return { changed: "transport" };
  }

  redo(context: AdamCommandContext): AdamCommandResult {
    return this.execute(context);
  }

  canMergeWith(command: AdamCommand): boolean {
    return command instanceof SetBpmCommand;
  }

  merge(command: AdamCommand): AdamCommand {
    if (!(command instanceof SetBpmCommand) || !this.canMergeWith(command)) {
      return this;
    }
    return new SetBpmCommand(this.from, command.to);
  }
}

export class SetStepDivisionCommand implements AdamCommand {
  readonly id = "setStepDivision";
  readonly label = "Grid";

  constructor(
    readonly from: number,
    readonly to: number,
  ) {}

  execute(context: AdamCommandContext): AdamCommandResult {
    requireArrange(context).setStepDivision(this.to);
    return { changed: "transport" };
  }

  undo(context: AdamCommandContext): AdamCommandResult {
    requireArrange(context).setStepDivision(this.from);
    return { changed: "transport" };
  }

  redo(context: AdamCommandContext): AdamCommandResult {
    return this.execute(context);
  }

  canMergeWith(command: AdamCommand): boolean {
    return command instanceof SetStepDivisionCommand;
  }

  merge(command: AdamCommand): AdamCommand {
    if (!(command instanceof SetStepDivisionCommand) || !this.canMergeWith(command)) {
      return this;
    }
    return new SetStepDivisionCommand(this.from, command.to);
  }
}

export class SetClipLoopCommand implements AdamCommand {
  readonly id = "setClipLoop";
  readonly label = "Song loop";

  constructor(
    readonly from: boolean,
    readonly to: boolean,
  ) {}

  execute(context: AdamCommandContext): AdamCommandResult {
    requireArrange(context).setClipLoop(this.to);
    return { changed: "transport" };
  }

  undo(context: AdamCommandContext): AdamCommandResult {
    requireArrange(context).setClipLoop(this.from);
    return { changed: "transport" };
  }

  redo(context: AdamCommandContext): AdamCommandResult {
    return this.execute(context);
  }
}
